import { prettyError } from '@grin-rnd/grin-api-sdk/dist/utils/ErrorUtils'
import { logError, logInfo } from './logUtils'

/**
 *
 * @param {Object} args
 * @param {Array<{items: Array<any>}>} args.pages - The InfiniteQuery pages list.
 * @param {Function} args.predicate - A function that receives an item and returns a boolean. If the function returns `true`, the item will be updated.
 * @param {Object} args.itemPatch - The object with the properties to be patched.
 */
export const patchPaginatedGraphQueryState = ({ pages = [], predicate = item => true, itemPatch = {} }) => {
  pages.forEach(page => {
    page.items.forEach(item => {
      if (predicate(item)) {
        Object.assign(item, { ...item, ...itemPatch })
      }
    })
  })
}

/**
 *
 * @param {Object} args
 * @param {Array<{items: Array<any>}>} args.pages - The InfiniteQuery pages list.
 * @param {Function} args.predicate - A function that receives an item and returns a boolean. If the function returns `true`, the item will be removed.
 */
export const removeFromPaginatedGraphQueryState = ({ pages = [], predicate = () => false }) => {
  pages.forEach(page => {
    page.items = page.items.filter(item => !predicate(item))
  })
}

/**
 * Logs the outcome (success / error) and duration of an API endpoint.
 * @param {Object} args
 * @param {Promise} args.queryFulfilled - The query promise from RTK.
 * @param {string} args.endpointName - The name of the endpoint.
 * @param {Object} [args.extraParams={}] - Extra parameters, e.g. the query arguments.
 * @param {number} [args.startTime=Date.now()] - Dispatch time in ms. Pass this when the profiler is
 *   called AFTER already awaiting queryFulfilled (e.g. to inspect the error first), so `took` still spans
 *   dispatch→settle instead of collapsing to ~0. Defaults to call time, unchanged for first-statement callers.
 */
export const apiEndpointProfiler = async ({
  queryFulfilled,
  endpointName,
  extraParams = {},
  startTime = Date.now()
}) => {
  try {
    await queryFulfilled
    const took = new Date().getTime() - startTime
    logInfo(`${endpointName}: request resolved successfully. took: ${took}`, {
      ...extraParams,
      took
    })
  } catch (ex) {
    const took = new Date().getTime() - startTime
    const normalizedError = ex?.error || ex
    logError(`${endpointName}: request rejected`, {
      ...extraParams,
      error: prettyError(normalizedError),
      took
    })
  }
}
